// @flow
import * as React from 'react';
import type { PermitDocument as PermitDocumentType } from 'esm-web-pro/types/networks';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import DownloadIcon from 'esm-web-pro/components/layout/sidePanel/icons/DownloadIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import { IntlShape, injectIntl, defineMessages } from 'react-intl';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import { ApiClient } from 'speed-js-core';
import type { UUID } from 'esm-web-pro/types/common';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import { withConfirmation } from 'speed-js-react';
import type { Confirmation } from 'speed-js-react';
import { MapContext } from 'esm-web-pro/containers/map/context';


type Props = {
    document: PermitDocumentType,
    intl: IntlShape,
    deleteDocument: Function,
    reload: Function,
    confirm: (confirmation: Confirmation) => void,
    buildName?: Function,
};

const msg = defineMessages({
    deleteTitle: {
        id: 'delete-document-title',
        defaultMessage: 'Are you sure you want to delete the permit document?',
    },
    cancel: {
        id: 'cancel',
        defaultMessage: 'Cancel',
    },
    confirm: {
        id: 'confirm',
        defaultMessage: 'Confirm',
    },
});

const PermitDocument = (props: Props) => {
    const {
        document, intl: { formatMessage }, deleteDocument, reload, confirm, buildName,
    } = props;

    const { is_draft: isDraft } = React.useContext(MapContext);

    const handleDelete = (e: SyntheticMouseEvent<HTMLButtonElement>) => {
        confirm({
            message: formatMessage(msg.deleteTitle),
            actions: {
                cancel: {
                    label: formatMessage(msg.cancel),
                    callback: () => {},
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(msg.confirm),
                    callback: () => {
                        if (deleteDocument) {
                            deleteDocument(document.uuid).then(() => {
                                reload();
                            });
                        }
                    },
                    type: 'positive',
                },
            },
        });
    };

    const documentActions = [
        {
            key: `document-${document.id}-more`,
            icon: <DownloadIcon />,
            size: '20px',
            width: '3px',
            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                window.open(document.file);
            },
            tooltip: formatMessage(actionMessages.download),
        },
    ];

    if (isDraft) {
        documentActions.push({
            key: `document-${document.id}-delete`,
            icon: <DeleteIcon />,
            size: '20px',
            width: '3px',
            callback: handleDelete,
            tooltip: formatMessage(actionMessages.delete),
        });
    }

    return (
        <ContentRow
            actions={<RowActions actions={documentActions} />}
            rowId={`document-${document.id}`}
            hoverIndicator={null}
            padLevel={1}
        >
            <ValueText>
                {/* TODO: Once we're handling documents, this should be the document name and not type */}
                {buildName ? buildName(document.document_type.name) : document.document_type.name}
            </ValueText>
        </ContentRow>
    );
};

PermitDocument.defaultProps = {
    buildName: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    deleteDocument: {
        send: (uuid: UUID) => apiClient.resources.infrastructureDocumentPermit.delete(uuid),
    },
});


export default withConfirmation(connectAPI(apiCallback)(injectIntl(PermitDocument)));
